import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Clock, Unlock, ChevronDown, ChevronUp } from 'lucide-react';
import { PageBanner } from '../components/PageBanner';
import { AnimatedSection } from '../components/AnimatedSection';
import { openContactModal } from '../components/ContactModal';
type FAQItem = {
  id: string;
  question: string;
  answer: string | React.ReactNode;
};
type FAQCategory = {
  id: string;
  title: string;
  items: FAQItem[];
};
const faqData: FAQCategory[] = [{
  id: 'about',
  title: 'ABOUT THE FIRETREE FUND',
  items: [{
    id: 'q1',
    question: 'How is this different from other intermediaries or advisory platforms?',
    answer: <div className="space-y-4" data-id="element-223">
            <p data-id="element-224">
              Firetree does not operate as a marketplace or introduction
              service.
            </p>
            <p data-id="element-225">
              We fund first and share later. Every organisation in the Fund
              portfolio has been supported by Firetree through a multi-year
              relationship. Our judgment is shaped by sustained engagement, site
              visits, operational conversations, and real partnership, not
              proposal review alone. We also operate nonprofit programmes
              ourselves in education and child protection, ensuring that our
              evaluation of partners is grounded in lived practice rather than
              observation.
            </p>
            <p data-id="element-226">
              The Firetree Fund is not a replacement for our own funding. We
              continue to back every organisation in this portfolio directly, as
              we have for years. What the Fund offers other funders is something
              different: a way into organisations we know deeply, seen through
              the same lens we use ourselves. Rather than navigating the
              Southeast Asian nonprofit landscape from a distance, funders gain
              access to relationships, context, and operational understanding
              that took us years to build.
            </p>
            <p data-id="element-227">
              For funders newer to this region or to trust-based philanthropy,
              the Fund also offers a window into how we work: our approach to
              identifying organisations, building relationships over time, and
              making funding decisions grounded in practice rather than
              paperwork. We share not just who we fund, but why, and how we came
              to know them well enough to back them with confidence.
            </p>
          </div>
  }, {
    id: 'q2',
    question: 'Is the Firetree Fund a pooled fund?',
    answer: <div className="space-y-4" data-id="element-228">
            <p data-id="element-229">
              The Firetree Fund is a co-funding opportunity, not a vehicle where
              contributions disappear into a collective pot.
            </p>
            <p data-id="element-230">
              When you give through the Fund, you are joining Firetree in
              backing organisations we already fund ourselves. You will always
              know which organisations your contribution is supporting and
              receive communication accordingly.
            </p>
            <p data-id="element-231">
              Allocation decisions are made by the Firetree Board, advised by a
              regional panel of technical experts from across Southeast Asia.
              That is deliberate. The value of the Fund rests on the consistency
              and integrity of our judgment, built over years of real
              partnership with these organisations. Funders who participate are,
              in effect, trusting Firetree to apply that judgment on their
              behalf, the same judgment we apply to our own giving.
            </p>
          </div>
  }, {
    id: 'q3',
    question: 'Is Firetree a fundraiser?',
    answer: <div className="space-y-4" data-id="element-232">
            <p data-id="element-233">Firetree is a funder, not a fundraiser.</p>
            <p data-id="element-234">
              We are not running a drive to raise money on behalf of the
              organisations we support. What we are doing is simpler: sharing
              access to organisations we have found, vetted, and backed through
              our own funding, with other funders who are interested in the same
              issues and geographies.
            </p>
            <p data-id="element-235">
              We believe the time and energy of nonprofit leaders is best spent
              on their work and the communities they serve, not on running
              fundraising drives. If we, as existing supporters of these
              organisations, can help other aligned funders understand what we
              know about them and why we back them, then we have removed some of
              that burden without asking anything of the organisations
              themselves.
            </p>
            <p data-id="element-236">
              The Fund is an extension of how we already work. It is not a
              fundraising mechanism. It is an offer to share our view.
            </p>
          </div>
  }, {
    id: 'q4',
    question: 'Is the Firetree Fund registered as a legal entity? What is ACF?',
    answer: <div className="space-y-4" data-id="element-237">
            <p data-id="element-238">
              The Firetree Fund operates as an initiative of the Firetree Group.
              For funders who wish to give through a managed vehicle,
              contributions are housed on the Asia Community Foundation (ACF)
              platform, a registered philanthropic platform that handles the
              legal and administrative infrastructure. You can learn more about
              ACF at{' '}
              <a href="https://asiacf.org" target="_blank" rel="noopener noreferrer" className="text-brand-red hover:underline" data-id="element-239">
                asiacf.org
              </a>
              .
            </p>
            <p data-id="element-240">
              Firetree manages the allocation process and funder engagement.
              Funders contributing through ACF benefit from the platform's
              compliance and governance framework while Firetree retains
              responsibility for portfolio judgment.
            </p>
          </div>
  }, {
    id: 'q5',
    question: 'Is there a minimum contribution?',
    answer: <div className="space-y-4" data-id="element-241">
            <p data-id="element-242">
              Yes. The Firetree Fund is designed for value-aligned funders
              committed to long-term, unrestricted giving. The minimum
              contribution is USD 100,000 per year.
            </p>
            <p data-id="element-243">
              If you are interested in engaging with our portfolio organisations
              in a different capacity, we are happy to have a conversation and
              make direct introductions where it makes sense.
            </p>
          </div>
  }, {
    id: 'q6',
    question: 'Can the Fund accept contributions from outside Singapore or Asia?',
    answer: <div className="space-y-4" data-id="element-244">
            <p data-id="element-245">
              Yes. The Firetree Fund welcomes contributions from value-aligned
              funders globally. There is no geographic restriction on who can
              give through the Fund.
            </p>
            <p data-id="element-246">
              All new funders go through ACF's Know Your Donor process before
              contributions are accepted. This is a standard due diligence
              procedure covering the source of funds and anti-money laundering
              requirements. We would guide you through what that involves as
              part of our initial conversations.
            </p>
          </div>
  }]
}, {
  id: 'selection',
  title: 'HOW ORGANISATIONS ARE SELECTED AND THE PORTFOLIO',
  items: [{
    id: 'q7',
    question: 'How does Firetree identify and select organisations for the portfolio?',
    answer: <div className="space-y-4" data-id="element-247">
            <p data-id="element-248">
              There is no open application process. Organisations in the Fund
              portfolio are drawn exclusively from Firetree's own portfolio of
              partners, built over 20 years of direct funding and engagement in
              Southeast Asia.
            </p>
            <p data-id="element-249">
              For a full picture of how we identify, assess, and build
              relationships with organisations, visit our How We Partner page.
            </p>
          </div>
  }, {
    id: 'q8',
    question: 'Who approves which organisations enter the Fund portfolio?',
    answer: <p data-id="element-250">
            All portfolio inclusions are approved by the Firetree Board, advised
            by a regional panel of technical experts spanning child protection,
            education, and nonprofit operations across Southeast Asia, chaired
            by Nicky Wilkinson.
          </p>
  }, {
    id: 'q9',
    question: 'Are all the organisations in the portfolio working on child protection, or are there other focus areas?',
    answer: <div className="space-y-4" data-id="element-251">
            <p data-id="element-252">
              Child protection is the primary focus of the Firetree Fund and the
              area where Firetree has the deepest direct experience. That said,
              we care about organisations doing strong, grounded work across the
              geographies we operate in and in issues that are closely connected
              to the protection and development of children and families.
            </p>
            <p data-id="element-253">
              The portfolio therefore also includes organisations working on
              innovations in education. This reflects both the reality that
              child protection and education are deeply intertwined in the lives
              of the families we seek to support, and our own direct experience
              running Starfish Education Foundation, a wholly owned and operated
              initiative of Firetree in Thailand.
            </p>
          </div>
  }, {
    id: 'q10',
    question: 'What happens if an organisation in the portfolio faces a governance crisis or significant leadership change?',
    answer: <div className="space-y-4" data-id="element-254">
            <p data-id="element-255">
              In our experience, significant governance issues or organisational
              crises are rarely a surprise. Because of the frequency and depth
              of our engagement with portfolio organisations, we are almost
              always able to see early indicators of something changing or going
              wrong, long before it becomes a crisis. This is something we have
              navigated across a number of organisations over the years. In the
              rare event that something were to change significantly enough to
              warrant flagging to funders, we would do so directly and promptly.
            </p>
            <p data-id="element-256">
              It is also worth noting that the organisations in this portfolio
              have been selected precisely because of their history and
              strength. They have been through a rigorous assessment across
              programmes, operations, and leadership using the Firetree rubric,
              and have also demonstrated their resilience and integrity through
              years of real funding relationship with us. These are not
              organisations we have recently discovered. They are organisations
              we know well.
            </p>
            <p data-id="element-257">
              In the event that something were to change significantly, we would
              not handle it at a distance. We would have direct conversations
              with the organisation and, where appropriate, with funders
              themselves to ensure everyone has the context they need to respond
              thoughtfully.
            </p>
          </div>
  }]
}, {
  id: 'funding',
  title: 'HOW FUNDING WORKS',
  items: [{
    id: 'q11',
    question: 'Can I direct my contribution to a specific organisation or country?',
    answer: <div className="space-y-4" data-id="element-258">
            <p data-id="element-259">
              Funders who contribute through the ACF-managed vehicle allow
              Firetree to allocate across the portfolio based on ongoing
              engagement with each organisation, their current needs, and their
              potential to grow or deepen their work. This model is designed for
              funders who want Firetree's judgment to guide deployment.
            </p>
            <p data-id="element-260">
              For funders who prefer to engage with a specific organisation or
              geography, we facilitate direct funding relationships, including
              introductions, visits, and ongoing dialogue. In both cases,
              Firetree continues to advocate for unrestricted, long-term giving.
            </p>
          </div>
  }, {
    id: 'q12',
    question: 'How does Firetree avoid creating dependency?',
    answer: <div className="space-y-4" data-id="element-261">
            <p data-id="element-262">
              We encourage portfolio organisations to maintain diversified
              funding sources. Firetree's own funding guidelines discourage any
              single donor from representing more than 25% of an established
              organisation's income. Contributions through the Fund are intended
              to strengthen systems and resilience, not create reliance on a
              single source.
            </p>
            <p data-id="element-263">
              In exceptional crisis situations, such as COVID-19, temporary
              concentrated support may be appropriate, but this is not the
              standard model.
            </p>
          </div>
  }]
}, {
  id: 'diligence',
  title: 'DUE DILIGENCE AND IMPACT',
  items: [{
    id: 'q13',
    question: 'Why does practitioner experience matter in child protection?',
    answer: <div className="space-y-4" data-id="element-264">
            <p data-id="element-265">
              Child protection is technical work. The quality of case
              management, trauma-informed care, safe reintegration decisions,
              and coordination with legal and government authorities is
              difficult to evaluate from documentation alone.
            </p>
            <p data-id="element-266">
              Because Firetree operates and funds child protection programmes
              directly, including through TCI in Manila, advised by a senior
              technical advisor with over 40 years of frontline experience, we
              assess partners with a practical understanding of what
              high-quality protection work requires. This practitioner-informed
              lens strengthens portfolio integrity.
            </p>
          </div>
  }, {
    id: 'q14',
    question: 'How does Firetree assess and measure impact across the portfolio?',
    answer: <div className="space-y-4" data-id="element-267">
            <p data-id="element-268">
              Firetree does not impose a standardised metrics framework across
              all organisations. We recognise that the populations these
              organisations serve are among the most marginalised, and that
              measurement in child protection and community development requires
              nuance, not just numbers.
            </p>
            <p data-id="element-269">
              Our approach to understanding impact is built on sustained
              engagement through visits, operational conversations, review of
              internal reporting, and participation in key discussions. We look
              for organisations that have honest learning cultures, adapt their
              models based on what they observe, and can articulate both what is
              working and what is not.
            </p>
            <p data-id="element-270">
              For funders who require specific outcome data, we are happy to
              discuss what each portfolio organisation tracks and how they
              report against their own objectives. We also share the standard
              annual reports of each organisation with all funders.
            </p>
          </div>
  }, {
    id: 'q15',
    question: 'Are the organisations in the portfolio externally audited?',
    answer: <p data-id="element-271">
            We do not mandate external audits beyond what the regulations and
            compliance requirements of each organisation's country of operation
            already require. Where an audit is required by law or by the
            organisation's own governance framework, we review it as part of our
            ongoing engagement. The strength of financial management systems and
            internal controls is something we assess closely regardless of
            whether a formal external audit is in place.
          </p>
  }]
}, {
  id: 'working',
  title: 'WORKING WITH FIRETREE AS A FUNDER',
  items: [{
    id: 'q16',
    question: 'What can funders expect in terms of communication and reporting?',
    answer: <div className="space-y-4" data-id="element-272">
            <p data-id="element-273">
              Funders receive a communication outlining how contributions have
              been allocated across the portfolio. Alongside this, we share each
              organisation's annual report and any other formal reporting that
              organisations provide to their funders.
            </p>
            <p data-id="element-274">
              In addition, funders receive an annual perspective from the
              Firetree team based on our direct interactions, visits, and
              conversations with portfolio organisations throughout the year.
              This is not a summary of documents. It is our view, grounded in
              the relationships we maintain with these organisations day to day.
            </p>
          </div>
  }, {
    id: 'q17',
    question: 'Can I visit the organisations myself?',
    answer: <div className="space-y-4" data-id="element-275">
            <p data-id="element-276">
              Where there is a specific need or interest, we work together with
              the organisation to decide whether a visit is appropriate and
              facilitate it accordingly. In cases where a direct relationship
              has been established between a funder and a portfolio
              organisation, we would prefer the organisation to make the
              decision on visits independently.
            </p>
            <p data-id="element-277">
              That said, the Firetree team visits portfolio organisations
              regularly as part of how we work. Where it makes sense, we are
              happy to open those visits to funders who wish to accompany us.
              This can often be the most natural and least disruptive way to see
              the work firsthand and understand it in context.
            </p>
          </div>
  }]
}];
export function EngagePage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const toggleQuestion = (id: string) => {
    setOpenId(openId === id ? null : id);
  };
  return <div className="pb-24" data-id="element-278">
      <PageBanner title="Engage with us" subtitle="Partner with the Firetree Fund" image="https://cdn.magicpatterns.com/uploads/3xLkYvXr5XqeBT956oRjkv/Teach_for_the_Philippines_-_Website_Banner_-_Fellowship_Program_(VVES).jpg" data-id="element-279" />

      {/* Our Principles Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-280">
        <div className="max-w-3xl mb-16" data-id="element-281">
          <h2 className="text-3xl font-serif text-brand-charcoal mb-6" data-id="element-282">
            Our principles
          </h2>
          <p className="text-xl text-brand-brown leading-relaxed" data-id="element-283">
            Every grant made as part of and managed by the Firetree Fund is
            built on three principles. These principles are not aspirational but
            the conditions under which we believe meaningful, lasting impact is
            possible.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-284">
          {/* Principle 1 */}
          <div className="bg-brand-cream p-8 rounded-2xl shadow-sm border border-brand-shape/50 flex flex-col items-center text-center" data-id="element-285">
            <div className="w-14 h-14 rounded-full bg-brand-card flex items-center justify-center mb-6 icon-hover" data-id="element-286">
              <Heart className="w-6 h-6 text-brand-gold" strokeWidth={1} data-id="element-287" />
            </div>
            <h3 className="text-xl font-serif text-brand-charcoal mb-4" data-id="element-288">
              Relationship-based
            </h3>
            <p className="text-brand-brown leading-relaxed" data-id="element-289">
              Trust is the foundation of everything we do. We only fund
              organisations we know deeply, in geographies where we have
              extensive direct experience.
            </p>
          </div>

          {/* Principle 2 */}
          <div className="bg-brand-cream p-8 rounded-2xl shadow-sm border border-brand-shape/50 flex flex-col items-center text-center" data-id="element-290">
            <div className="w-14 h-14 rounded-full bg-brand-card flex items-center justify-center mb-6 icon-hover" data-id="element-291">
              <Clock className="w-6 h-6 text-brand-gold" strokeWidth={1} data-id="element-292" />
            </div>
            <h3 className="text-xl font-serif text-brand-charcoal mb-4" data-id="element-293">
              Long-term
            </h3>
            <p className="text-brand-brown leading-relaxed" data-id="element-294">
              We support our partners for as long as they need it to achieve
              their goals. Short funding cycles consume the energy that leaders
              should be spending on their work.
            </p>
          </div>

          {/* Principle 3 */}
          <div className="bg-brand-cream p-8 rounded-2xl shadow-sm border border-brand-shape/50 flex flex-col items-center text-center" data-id="element-295">
            <div className="w-14 h-14 rounded-full bg-brand-card flex items-center justify-center mb-6 icon-hover" data-id="element-296">
              <Unlock className="w-6 h-6 text-brand-gold" strokeWidth={1} data-id="element-297" />
            </div>
            <h3 className="text-xl font-serif text-brand-charcoal mb-4" data-id="element-298">
              Unrestricted
            </h3>
            <p className="text-brand-brown leading-relaxed" data-id="element-299">
              Once trust is established, we believe the organisation's
              leadership knows best where resources are most needed. We do not
              prescribe how funding is used.
            </p>
          </div>
        </div>
      </section>

      {/* Two Ways to Participate Section */}
      <section className="py-24 bg-brand-card/30 mt-12" data-id="element-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-301">
          <div className="max-w-3xl mb-16" data-id="element-302">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-303">
              Two ways to participate
            </h2>
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-304">
              Whether you want to give through a managed vehicle or engage
              directly with specific organisations, we can facilitate both. In
              either case, Firetree continues to advocate for unrestricted,
              long-term giving throughout the relationship.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-305">
            {/* Route 1 */}
            <div className="bg-brand-cream p-10 rounded-2xl shadow-sm border-t-4 border-t-brand-gold border-x border-b border-brand-shape/50" data-id="element-306">
              <span className="text-sm font-bold text-brand-gold tracking-widest uppercase mb-4 block" data-id="element-307">
                Route 1
              </span>
              <h3 className="text-2xl font-serif text-brand-charcoal mb-6" data-id="element-308">
                Through the Firetree Fund via ACF
              </h3>
              <p className="text-brand-brown leading-relaxed text-lg" data-id="element-309">
                Funds are housed with the Asia Community Foundation and managed
                by Firetree. We determine allocation across the portfolio based
                on ongoing engagement with each organisation, their current
                needs and potential to grow or deepen their work. Funders
                receive structured communication on allocation, and an annual
                supplementary report grounded in our visits and engagement.
              </p>
            </div>

            {/* Route 2 */}
            <div className="bg-brand-charcoal p-10 rounded-2xl shadow-sm border-t-4 border-t-brand-red" data-id="element-310">
              <span className="text-sm font-bold text-brand-red tracking-widest uppercase mb-4 block" data-id="element-311">
                Route 2
              </span>
              <h3 className="text-2xl font-serif text-brand-cream mb-6" data-id="element-312">
                Direct funding relationship
              </h3>
              <p className="text-brand-shape leading-relaxed text-lg" data-id="element-313">
                For funders who want to engage more closely with specific
                organisations or prefer to give to specific programmes or
                geography. Firetree facilitates introductions, visits and
                continued dialogue, and provides the context we have built over
                years of partnership. We stay available throughout and continue
                to advocate for the kind of funding these organisations need
                most.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center flex items-center justify-center" data-id="element-314">
        <div className="flex flex-col items-center space-y-8" data-id="element-315">
          <button onClick={openContactModal} className="inline-flex items-center text-xl text-brand-charcoal font-serif hover:text-brand-red transition-colors group" data-id="element-316">
            Have questions? Get in touch
            <ArrowRight className="ml-3 w-6 h-6 transform group-hover:translate-x-1 transition-transform text-brand-red" data-id="element-317" />
          </button>

          <button onClick={openContactModal} className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-brand-cream bg-brand-red rounded-full hover:bg-brand-charcoal transition-colors shadow-sm hover:shadow-md" data-id="element-318">
            Get in touch
          </button>
        </div>
      </section>

      {/* Full FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-319">
        <AnimatedSection data-id="element-320">
          <h2 className="text-3xl font-serif text-brand-charcoal mb-16 text-center" data-id="element-321">
            Frequently Asked Questions
          </h2>
        </AnimatedSection>

        <div className="space-y-16" data-id="element-322">
          {faqData.map(category => <AnimatedSection key={category.id} data-id="element-323">
              <h3 className="text-sm font-bold tracking-widest uppercase text-brand-gold mb-6" data-id="element-324">
                {category.title}
              </h3>

              <div className="border-t border-brand-shape/50" data-id="element-325">
                {category.items.map(item => {
              const isOpen = openId === item.id;
              return <div key={item.id} className="border-b border-brand-shape/50" data-id="element-326">
                      <button onClick={() => toggleQuestion(item.id)} className="w-full py-6 flex justify-between items-center text-left focus:outline-none group" aria-expanded={isOpen} data-id="element-327">
                        <span className="text-lg font-semibold text-brand-charcoal pr-8 group-hover:text-brand-red transition-colors" data-id="element-328">
                          {item.question}
                        </span>
                        <span className="flex-shrink-0 text-brand-gold" data-id="element-329">
                          {isOpen ? <ChevronUp className="w-6 h-6" data-id="element-330" /> : <ChevronDown className="w-6 h-6" data-id="element-331" />}
                        </span>
                      </button>

                      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1000px] opacity-100 pb-8' : 'max-h-0 opacity-0'}`} data-id="element-332">
                        <div className="text-brand-brown leading-relaxed text-lg" data-id="element-333">
                          {item.answer}
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </AnimatedSection>)}
        </div>
      </section>
    </div>;
}